import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWalletRounded,
  CreditCardRounded,
  DeleteOutlineRounded,
  PaymentsRounded,
  ReceiptLongRounded,
  RestartAltRounded,
  SwapHorizRounded,
} from '@mui/icons-material';

const money = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function toPositiveInteger(value) {
  const parsed = Number(String(value ?? '').replace(/[^0-9]/g, ''));
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function isCashMethod(method) {
  return Boolean(method?.permiteVuelto) || normalizeKey(method?.clave).includes('EFECTIVO');
}

function methodMayUseReference(method) {
  const key = normalizeKey(method?.clave);
  return Boolean(
    method?.requiereReferencia ||
    method?.requiere_referencia ||
    method?.usaTerminal ||
    method?.usa_terminal ||
    method?.usaPasarela ||
    method?.usa_pasarela ||
    key.includes('TRANSBANK') ||
    key.includes('TARJETA') ||
    key.includes('TRANSFER') ||
    key.includes('BANCHILE')
  );
}

function roundCashLegCLP(value) {
  const amount = Math.round(Number(value) || 0);
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const remainder = abs % 10;

  if (remainder === 0) return amount;

  const roundedAbs = remainder <= 5
    ? abs - remainder
    : abs + (10 - remainder);

  return sign * roundedAbs;
}

function getMethodIcon(method) {
  const key = normalizeKey(method?.clave);
  if (isCashMethod(method)) return <AccountBalanceWalletRounded />;
  if (key.includes('TRANSFER') || key.includes('BANCHILE') || key.includes('BANCO')) return <SwapHorizRounded />;
  if (key.includes('TARJETA') || key.includes('TRANSBANK') || key.includes('DEBITO') || key.includes('CREDITO')) return <CreditCardRounded />;
  if (methodMayUseReference(method)) return <ReceiptLongRounded />;
  return <PaymentsRounded />;
}

function paymentIdentity(method) {
  return String(
    method?.payment_relation_id ??
    method?.relationId ??
    method?.relation_id ??
    method?.payment_method_id ??
    method?.methodId ??
    method?.metodo_pago_id ??
    method?.metodopago_id ??
    method?.id ??
    method?.clave ??
    ''
  );
}

function paymentMethodId(method) {
  const value = method?.payment_method_id ?? method?.methodId ?? method?.method_id ?? method?.metodo_pago_id ?? method?.metodopago_id ?? null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function paymentConditionId(method) {
  const value = method?.payment_condition_id ?? method?.conditionId ?? method?.condition_id ?? method?.condicion_pago_id ?? method?.condicionpago_id ?? null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function paymentRelationId(method) {
  const value = method?.payment_relation_id ?? method?.relationId ?? method?.relation_id ?? method?.relacion_id ?? method?.configuracion_metodo_condicion_id ?? null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export default function PaymentModal({
  open,
  onClose,
  total,
  metodosPago = [],
  metodoSeleccionado,
  onSeleccionarMetodo,
  onConfirmar,
  procesando = false,
}) {
  const [paso, setPaso] = useState('metodo');
  const [pagos, setPagos] = useState([]);
  const [montoActual, setMontoActual] = useState('');
  const [recibido, setRecibido] = useState('');
  const [referencia, setReferencia] = useState('');
  const [error, setError] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const amountInputRef = useRef(null);
  const cashInputRef = useRef(null);
  const referenceInputRef = useRef(null);
  const initializedOpenRef = useRef(false);

  const totalFiscal = Math.max(0, Math.round(Number(total || 0)));
  const bloqueado = procesando || confirmando;

  const metodos = useMemo(
    () => (Array.isArray(metodosPago)
      ? metodosPago
        .filter((method) => method?.activo !== false && method?.visiblePos !== false)
        .sort((a, b) => Number(a?.orden || 0) - Number(b?.orden || 0))
      : []),
    [metodosPago]
  );

  const totalAplicado = useMemo(
    () => pagos.reduce((sum, payment) => sum + Number(payment?.amount_applied || 0), 0),
    [pagos]
  );

  const saldo = Math.max(0, totalFiscal - totalAplicado);

  const idsUsados = useMemo(() => new Set(pagos.map((payment) => String(payment.identity))), [pagos]);

  const metodosDisponibles = useMemo(() => metodos.filter((method) => {
    if (idsUsados.has(paymentIdentity(method))) return false;
    if (pagos.length > 0 && isCashMethod(method)) return false;
    return true;
  }), [idsUsados, metodos, pagos.length]);

  const metodo = useMemo(() => {
    const selected = metodosDisponibles.find((method) => String(method.id) === String(metodoSeleccionado));
    return selected || metodosDisponibles[0] || null;
  }, [metodoSeleccionado, metodosDisponibles]);

  const indiceMetodo = useMemo(() => {
    const index = metodosDisponibles.findIndex((method) => String(method.id) === String(metodo?.id));
    return index >= 0 ? index : 0;
  }, [metodo?.id, metodosDisponibles]);

  const esEfectivo = isCashMethod(metodo);
  const muestraReferencia = methodMayUseReference(metodo);
  const valorRecibido = toPositiveInteger(recibido);
  const valorMontoActual = Math.min(toPositiveInteger(montoActual), saldo);
  const efectivoObjetivo = roundCashLegCLP(saldo);
  const efectivoCierraSaldo = esEfectivo && saldo > 0 && valorRecibido >= efectivoObjetivo;
  const efectivoAplicado = efectivoCierraSaldo ? saldo : Math.min(valorRecibido, saldo);
  const efectivoPagado = efectivoCierraSaldo ? efectivoObjetivo : efectivoAplicado;
  const vueltoActual = efectivoCierraSaldo ? Math.max(valorRecibido - efectivoPagado, 0) : 0;
  const redondeoActual = efectivoCierraSaldo ? efectivoPagado - efectivoAplicado : 0;
  const efectivoParcialInvalido = esEfectivo && valorRecibido > 0 && !efectivoCierraSaldo && (valorRecibido % 10 !== 0);

  const seleccionarMetodoPorIndice = useCallback((index) => {
    if (!metodosDisponibles.length) return;
    const safeIndex = (index + metodosDisponibles.length) % metodosDisponibles.length;
    const next = metodosDisponibles[safeIndex];
    if (next) onSeleccionarMetodo?.(next.id);
  }, [metodosDisponibles, onSeleccionarMetodo]);

  const resetSequence = useCallback(() => {
    if (bloqueado) return;
    setPagos([]);
    setPaso('metodo');
    setMontoActual(String(totalFiscal));
    setRecibido('');
    setReferencia('');
    setError('');

    const preferred = metodos.find((method) => isCashMethod(method)) || metodos[0] || null;
    if (preferred) onSeleccionarMetodo?.(preferred.id);
  }, [bloqueado, metodos, onSeleccionarMetodo, totalFiscal]);

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return;
    }

    // Inicializar una sola vez por cada apertura del modal.
    // Cambiar de medio durante un pago mixto NO debe borrar los tramos ya aplicados.
    if (initializedOpenRef.current) return;
    initializedOpenRef.current = true;

    setPagos([]);
    setPaso('metodo');
    setMontoActual(String(totalFiscal));
    setRecibido('');
    setReferencia('');
    setError('');

    const selectedExists = metodos.some((method) => String(method.id) === String(metodoSeleccionado));
    if (!selectedExists && metodos.length) {
      const preferred = metodos.find((method) => isCashMethod(method)) || metodos[0];
      onSeleccionarMetodo?.(preferred.id);
    }
  }, [metodoSeleccionado, metodos, onSeleccionarMetodo, open, totalFiscal]);

  useEffect(() => {
    if (!open || paso !== 'metodo') return;
    if (!metodosDisponibles.length) return;

    const selectedStillAvailable = metodosDisponibles.some((method) => String(method.id) === String(metodoSeleccionado));
    if (!selectedStillAvailable) onSeleccionarMetodo?.(metodosDisponibles[0].id);
  }, [metodoSeleccionado, metodosDisponibles, onSeleccionarMetodo, open, paso]);

  const abrirDetalleMetodo = useCallback(() => {
    if (bloqueado || !metodo || saldo <= 0) return;
    setError('');
    setPaso('detalle');
    setReferencia('');

    if (isCashMethod(metodo)) {
      setRecibido(String(roundCashLegCLP(saldo)));
      setMontoActual('');
      window.setTimeout(() => {
        cashInputRef.current?.focus?.();
        cashInputRef.current?.select?.();
      }, 60);
      return;
    }

    setMontoActual(String(saldo));
    setRecibido('');
    window.setTimeout(() => {
      amountInputRef.current?.focus?.();
      amountInputRef.current?.select?.();
    }, 60);
  }, [bloqueado, metodo, saldo]);

  const finalizarSecuencia = useCallback(async (nextPagos) => {
    if (bloqueado) return;

    const vuelto = nextPagos.reduce((sum, payment) => sum + Number(payment.change_given || 0), 0);
    const redondeo = nextPagos.reduce((sum, payment) => sum + Number(payment.cash_rounding || 0), 0);

    setConfirmando(true);
    setError('');

    try {
      await Promise.resolve(onConfirmar?.({
        pagos: nextPagos,
        pagoMixto: nextPagos.length > 1,
        total: totalFiscal,
        totalAplicado: nextPagos.reduce((sum, payment) => sum + Number(payment.amount_applied || 0), 0),
        vuelto,
        redondeo,
        metodo: nextPagos.length === 1 ? nextPagos[0].metodo : null,
      }));
    } catch (submitError) {
      setError(submitError?.message || 'No se pudo registrar la venta. Puede reintentar sin duplicarla.');
    } finally {
      setConfirmando(false);
    }
  }, [bloqueado, onConfirmar, totalFiscal]);

  const registrarTramo = useCallback(() => {
    if (bloqueado || !metodo || saldo <= 0) return;
    setError('');

    let amountApplied = 0;
    let amountPaid = 0;
    let amountReceived = 0;
    let changeGiven = 0;
    let cashRounding = 0;

    if (isCashMethod(metodo)) {
      if (valorRecibido <= 0) {
        setError('Ingrese el efectivo recibido.');
        return;
      }
      if (efectivoParcialInvalido) {
        setError('Para un pago parcial en efectivo, ingrese un monto múltiplo de $10.');
        return;
      }

      amountApplied = efectivoAplicado;
      amountPaid = efectivoPagado;
      amountReceived = valorRecibido;
      changeGiven = vueltoActual;
      cashRounding = redondeoActual;
    } else {
      if (valorMontoActual <= 0) {
        setError('Ingrese un monto mayor a $0.');
        return;
      }

      if ((metodo?.requiereReferencia || metodo?.requiere_referencia) && !String(referencia || '').trim()) {
        setError('Este medio de pago requiere referencia o voucher.');
        window.setTimeout(() => referenceInputRef.current?.focus?.(), 40);
        return;
      }

      amountApplied = valorMontoActual;
      amountPaid = valorMontoActual;
      amountReceived = valorMontoActual;
    }

    if (amountApplied <= 0) return;

    const ref = String(referencia || '').trim() || null;
    const nextPayment = {
      identity: paymentIdentity(metodo),
      payment_relation_id: paymentRelationId(metodo),
      payment_method_id: paymentMethodId(metodo),
      payment_method_key: normalizeKey(metodo?.clave),
      payment_method_name: metodo?.nombre || metodo?.label || metodo?.clave || 'Pago',
      payment_condition_id: paymentConditionId(metodo),
      payment_condition_key: normalizeKey(metodo?.payment_condition_key || metodo?.condicion_pago_clave || metodo?.condicionpago_clave || metodo?.conditionKey),
      amount_applied: amountApplied,
      amount_paid: amountPaid,
      amount_received: amountReceived,
      change_given: changeGiven,
      cash_rounding: cashRounding,
      voucher_number: ref,
      reference: ref,
      referencia: ref,
      metodo,
    };

    const nextPagos = [...pagos, nextPayment];
    const nextApplied = nextPagos.reduce((sum, payment) => sum + Number(payment.amount_applied || 0), 0);
    const nextRemaining = Math.max(0, totalFiscal - nextApplied);

    if (nextRemaining <= 0) {
      finalizarSecuencia(nextPagos);
      return;
    }

    const used = new Set(nextPagos.map((payment) => String(payment.identity)));
    const candidates = metodos.filter((candidate) => !used.has(paymentIdentity(candidate)) && !isCashMethod(candidate));

    if (!candidates.length) {
      setError('Queda saldo pendiente, pero no hay otro medio de pago disponible.');
      return;
    }

    setPagos(nextPagos);
    setPaso('metodo');
    setMontoActual(String(nextRemaining));
    setRecibido('');
    setReferencia('');
    onSeleccionarMetodo?.(candidates[0].id);
  }, [
    bloqueado,
    efectivoAplicado,
    efectivoPagado,
    efectivoParcialInvalido,
    finalizarSecuencia,
    metodos,
    metodo,
    onSeleccionarMetodo,
    pagos,
    redondeoActual,
    referencia,
    saldo,
    totalFiscal,
    valorMontoActual,
    valorRecibido,
    vueltoActual,
  ]);

  const volver = useCallback(() => {
    if (bloqueado) return;
    setError('');
    if (paso === 'detalle') {
      setPaso('metodo');
      return;
    }
    onClose?.();
  }, [bloqueado, onClose, paso]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (bloqueado) {
        event.preventDefault();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        volver();
        return;
      }

      if (paso === 'metodo') {
        if (/^[1-9]$/.test(event.key)) {
          const shortcutIndex = Number(event.key) - 1;
          if (metodosDisponibles[shortcutIndex]) {
            event.preventDefault();
            onSeleccionarMetodo?.(metodosDisponibles[shortcutIndex].id);
          }
          return;
        }

        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          seleccionarMetodoPorIndice(indiceMetodo - 1);
          return;
        }

        if (['ArrowRight', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          seleccionarMetodoPorIndice(indiceMetodo + 1);
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          abrirDetalleMetodo();
        }
        return;
      }

      if (paso === 'detalle') {
        if (esEfectivo && event.key === 'ArrowUp') {
          event.preventDefault();
          setRecibido(String(valorRecibido + 1000));
          return;
        }

        if (esEfectivo && event.key === 'ArrowDown') {
          event.preventDefault();
          setRecibido(String(Math.max(0, valorRecibido - 1000)));
          return;
        }

        if (event.key === 'Enter') {
          const active = document.activeElement;
          const isReference = active === referenceInputRef.current;
          if (!isReference || !event.shiftKey) {
            event.preventDefault();
            registrarTramo();
          }
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [
    abrirDetalleMetodo,
    bloqueado,
    esEfectivo,
    indiceMetodo,
    metodosDisponibles,
    onSeleccionarMetodo,
    open,
    paso,
    registrarTramo,
    seleccionarMetodoPorIndice,
    valorRecibido,
    volver,
  ]);

  const paymentRows = pagos.map((payment, index) => ({
    ...payment,
    numero: index + 1,
  }));

  return (
    <Dialog
      open={open}
      onClose={() => { if (!bloqueado) onClose?.(); }}
      disableEscapeKeyDown={bloqueado}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ pb: 0.75 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="h6" fontWeight={950}>
              {paso === 'metodo' ? (pagos.length ? 'Complete el saldo' : 'Seleccione forma de pago') : esEfectivo ? 'Pago en efectivo' : 'Monto del pago'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {paso === 'metodo'
                ? '← → para cambiar · 1–9 acceso rápido · Enter para continuar'
                : 'Enter para aplicar · Esc para volver'}
            </Typography>
          </Box>
          {pagos.length > 0 && (
            <Button size="small" startIcon={<RestartAltRounded />} onClick={resetSequence} disabled={bloqueado}>
              Reiniciar
            </Button>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack spacing={1.4}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'end',
              gap: 1,
              px: 0.2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>TOTAL VENTA</Typography>
              <Typography variant="body2" fontWeight={900}>{money.format(totalFiscal)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>SALDO PENDIENTE</Typography>
              <Typography variant="h4" fontWeight={1000} color={saldo > 0 ? 'primary.main' : 'success.main'} sx={{ lineHeight: 1 }}>
                {money.format(saldo)}
              </Typography>
            </Box>
          </Box>

          {paymentRows.length > 0 && (
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden' }}>
              {paymentRows.map((payment, index) => (
                <React.Fragment key={`${payment.identity}-${index}`}>
                  {index > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.9 }}>
                    <Chip size="small" label={payment.numero} sx={{ minWidth: 28, fontWeight: 950 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={950} noWrap>
                        {payment.payment_method_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Aplicado {money.format(payment.amount_applied)}
                        {payment.reference ? ` · Ref. ${payment.reference}` : ''}
                      </Typography>
                    </Box>
                    <Typography fontWeight={1000}>{money.format(payment.amount_applied)}</Typography>
                  </Stack>
                </React.Fragment>
              ))}
            </Box>
          )}

          {error && <Alert severity="warning">{error}</Alert>}

          {paso === 'metodo' && (
            <>
              {pagos.length > 0 && (
                <Alert severity="info" sx={{ py: 0.35 }}>
                  Seleccione un medio distinto para completar los <strong>{money.format(saldo)}</strong> pendientes.
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                {metodosDisponibles.map((method, index) => {
                  const active = String(method.id) === String(metodo?.id);
                  return (
                    <Button
                      key={method.id || method.clave || index}
                      variant={active ? 'contained' : 'outlined'}
                      disabled={bloqueado}
                      color={isCashMethod(method) ? 'success' : 'primary'}
                      startIcon={getMethodIcon(method)}
                      onClick={() => onSeleccionarMetodo?.(method.id)}
                      onDoubleClick={() => {
                        onSeleccionarMetodo?.(method.id);
                        window.setTimeout(() => abrirDetalleMetodo(), 0);
                      }}
                      sx={{
                        minHeight: 68,
                        justifyContent: 'flex-start',
                        px: 1.7,
                        borderRadius: 2.5,
                        fontWeight: 900,
                        textTransform: 'none',
                      }}
                    >
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography fontWeight={950}>{index + 1}. {method.nombre || method.clave}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.82 }}>
                          {isCashMethod(method)
                            ? (pagos.length ? 'Efectivo no disponible después del primer tramo' : 'Permite pago total o parcial')
                            : 'Permite completar o dividir el saldo'}
                        </Typography>
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            </>
          )}

          {paso === 'detalle' && esEfectivo && (
            <>
              <Typography variant="body2" color="text.secondary">
                Ingrese el efectivo entregado. Si es menor al saldo, se aplicará como primer tramo y luego podrá completar con otro medio.
              </Typography>

              <TextField
                inputRef={cashInputRef}
                label="Efectivo recibido"
                disabled={bloqueado}
                value={recibido}
                onChange={(event) => setRecibido(event.target.value.replace(/\D/g, ''))}
                fullWidth
                autoFocus
                inputProps={{ inputMode: 'numeric', style: { fontSize: 28, fontWeight: 950, textAlign: 'center' } }}
              />

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {[1000, 2000, 5000, 10000, 20000].map((value) => (
                  <Button key={value} size="small" variant="outlined" disabled={bloqueado} onClick={() => setRecibido(String(value))}>
                    {money.format(value)}
                  </Button>
                ))}
                <Button size="small" variant="outlined" disabled={bloqueado} onClick={() => setRecibido(String(roundCashLegCLP(saldo)))}>
                  Saldo {money.format(roundCashLegCLP(saldo))}
                </Button>
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.1 }}>
                  <Typography variant="caption" color="text.secondary">Se aplica a la venta</Typography>
                  <Typography fontWeight={950}>{money.format(efectivoAplicado)}</Typography>
                </Box>
                <Box sx={{ bgcolor: vueltoActual > 0 ? '#ecfdf5' : '#f8fafc', borderRadius: 2, p: 1.1 }}>
                  <Typography variant="caption" color="text.secondary">Vuelto</Typography>
                  <Typography fontWeight={950} color={vueltoActual > 0 ? 'success.main' : 'text.primary'}>{money.format(vueltoActual)}</Typography>
                </Box>
              </Box>

              {redondeoActual !== 0 && (
                <Alert severity="info" sx={{ py: 0.3 }}>
                  Efectivo a cobrar: <strong>{money.format(efectivoPagado)}</strong> · redondeo {redondeoActual > 0 ? '+' : ''}{money.format(redondeoActual)}
                </Alert>
              )}
            </>
          )}

          {paso === 'detalle' && !esEfectivo && (
            <>
              <Typography variant="h6" fontWeight={950}>{metodo?.nombre || metodo?.clave || 'Pago'}</Typography>

              <TextField
                inputRef={amountInputRef}
                label="Monto a aplicar"
                disabled={bloqueado}
                value={montoActual}
                onChange={(event) => setMontoActual(event.target.value.replace(/\D/g, ''))}
                fullWidth
                autoFocus
                inputProps={{ inputMode: 'numeric', style: { fontSize: 26, fontWeight: 950, textAlign: 'center' } }}
                helperText={`Máximo disponible: ${money.format(saldo)}`}
              />

              <Stack direction="row" spacing={0.8}>
                <Button variant="outlined" disabled={bloqueado} onClick={() => setMontoActual(String(saldo))} sx={{ flex: 1 }}>
                  Usar saldo completo {money.format(saldo)}
                </Button>
              </Stack>

              {muestraReferencia && (
                <TextField
                  inputRef={referenceInputRef}
                  label={metodo?.requiereReferencia || metodo?.requiere_referencia ? 'Referencia / voucher' : 'Referencia / voucher (opcional)'}
                  disabled={bloqueado}
                  value={referencia}
                  onChange={(event) => setReferencia(event.target.value)}
                  fullWidth
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.4, pt: 1 }}>
        <Button onClick={volver} disabled={bloqueado}>{paso === 'detalle' ? 'Volver' : 'Cancelar'}</Button>
        {pagos.length > 0 && paso === 'metodo' && (
          <IconButton size="small" color="error" onClick={resetSequence} title="Reiniciar pagos" disabled={bloqueado}>
            <DeleteOutlineRounded />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={paso === 'metodo' ? abrirDetalleMetodo : registrarTramo}
          disabled={bloqueado || !metodo || saldo <= 0 || (paso === 'detalle' && esEfectivo && (valorRecibido <= 0 || efectivoParcialInvalido)) || (paso === 'detalle' && !esEfectivo && valorMontoActual <= 0)}
        >
          {bloqueado
            ? <><CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />Registrando venta…</>
            : paso === 'metodo'
              ? 'Continuar'
              : esEfectivo && efectivoCierraSaldo
                ? (vueltoActual > 0 ? `Confirmar · vuelto ${money.format(vueltoActual)}` : 'Confirmar pago')
                : (esEfectivo || valorMontoActual < saldo)
                  ? 'Aplicar y continuar'
                  : 'Confirmar pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
